/**
 * Синхронизация ref/path с URL query params для RepoPage.
 *
 * URL — source of truth для deep-link'ов («открой эту ветку этого
 * monorepo-подпроекта»). React Query кэширует snapshot по этой паре,
 * Top-bar показывает текущий ref, ProjectPicker — текущий path.
 *
 * Семантика `path`:
 *   - `params.has('path')` → URL содержит `?path=…`; значение
 *     возвращается как есть, включая литеральное `''` для корня
 *     monorepo.
 *   - `params.has('path')` false → URL без параметра; `path` =
 *     `undefined`. Это разрешается на уровне RepoPage по списку
 *     проектов (если single-project — берём его автоматически без
 *     записи в URL).
 *
 * Без `params.has`-проверки `params.get('path') ?? undefined` смешало
 * бы `''` и `undefined` — а это разные состояния (см. fix
 * single-project репо в корне, где `['']` в списке проектов был
 * незаписываемым в URL и приводил к бесконечному лоадеру).
 *
 * `setRef`/`setPath` пишут одной replace-навигацией (не push) — иначе
 * каждое переключение пухнет в history-стек, и кнопка «назад» работает
 * не как ожидает пользователь («назад» = HomePage, а не предыдущая ветка).
 */

import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface RepoUrlState {
    ref:  string | undefined;
    path: string | undefined;
    setRef:  (ref: string | undefined) => void;
    setPath: (path: string | undefined) => void;
}

export function useRepoUrlState(): RepoUrlState {
    const [params, setParams] = useSearchParams();
    const ref  = params.get('ref')  ?? undefined;
    const path = params.has('path') ? (params.get('path') ?? '') : undefined;

    const setRef = useCallback((next: string | undefined) => {
        setParams(prev => {
            const np = new URLSearchParams(prev);
            if (next) np.set('ref', next);
            else      np.delete('ref');
            return np;
        }, { replace: true });
    }, [setParams]);

    const setPath = useCallback((next: string | undefined) => {
        setParams(prev => {
            const np = new URLSearchParams(prev);
            // next === undefined → удалить параметр (вернуться к auto-resolve).
            // next === '' или 'foo' → записать в URL (явное «корень monorepo»
            // или конкретная поддиректория).
            if (next === undefined) np.delete('path');
            else                    np.set('path', next);
            return np;
        }, { replace: true });
    }, [setParams]);

    return { ref, path, setRef, setPath };
}
